import { subscribeKlaviyoList } from "analytics/klaviyo";
import KlaviyoTrackingService from "analytics/klaviyo/klaviyoEvents";
import onSubmitSubscriptionEmailModalApi from "api/contact/onSubmitSubscriptionEmailModalApi";
import classNames from "classnames";
import Form from "component/design-system/form/Form";
import Input from "component/design-system/form/Input";
import PAImage from "component/design-system/images/PAImage";
import PAText from "component/design-system/typography/PAText/PAText";
import Text from "component/design-system/typography/Text";
import { isFormEnabled, Validation } from "component/formsy-components/formsy-helper";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { createRef, useState } from "react";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import HideIf from "util/HideIf";
import { toLowerCase } from "../../util/lodash/toLowerCase";
import styles from "./newsletterFooter.module.scss";

function NewsletterFooter() {
  const [canSubmit, setCanSubmit] = useState(false);
  const [userAlreadyExist, setUserAlreadyExist] = useState(false);
  const [newsLetterSubscribed, setNewsLetterSubscribed] = useState(null);
  const formRef = createRef(null);
  const { t } = useTranslation("common");
  const router = useRouter();

  const submit = (model) => {
    const { email } = model;
    onSubmitSubscriptionEmailModalApi(toLowerCase(email))
      .then((response: any) => {
        if (response && response.data) {
          setNewsLetterSubscribed(true);
          if (response.data.alreadyExistingUser) {
            setUserAlreadyExist(true);
          } else {
            subscribeKlaviyoList(email, "footer", router.locale);
            KlaviyoTrackingService.identifyKlaviyoUser(email);
            setUserAlreadyExist(false);
          }
        }
      });
  };

  function handleOnFormChange() {
    const formValidations: Validation[] = [{ validationType: "IS_EMAIL", elementName: "email" }];
    const canSubmitForm = isFormEnabled(formRef, ["email"], formValidations);
    setCanSubmit(canSubmitForm);
  }

  let renderText = null;
  if (userAlreadyExist) {
    renderText = t("footer.Tag_line_newsletter_submit_existing");
  }
  if (newsLetterSubscribed && !userAlreadyExist) {
    renderText = t("footer.Tag_line_newsletter_submit_new");
  }

  const emailAltText = "Sign up to our newsletter to get special discounts";
  const promotionAltText = "Subscribe to our newsletter for exclusive promotions";

  return (
    <>
      <HideIf show={newsLetterSubscribed}>
        <div className="col-12 d-flex align-items-center">
          <PAImage
            src={getRelativeImagePath("/images/public/pa_footer_icon_check_email.svg")}
            width={80}
            height={80}
            alt={emailAltText}
            title={emailAltText}
            className={`${styles.expressDeliverySubmitted}`}
          />
          <Text size={3} className={`m-0 ${styles.heading}`}>{renderText}</Text>
        </div>
      </HideIf>
      <HideIf show={!newsLetterSubscribed}>
        <div className="col-md-7 col-xs-12 d-flex align-items-center">
          <div>
            <PAImage
              src={getRelativeImagePath("/images/public/pa_footer_icon_newsletter.svg")}
              alt={promotionAltText}
              title={promotionAltText}
              desktopHeight="calc(6.6 * var(--safari-root-font, 10px))"
              tabletHeight="calc(5 * var(--safari-root-font, 10px))"
              mobileHeight="calc(4 * var(--safari-root-font, 10px))"
              className={styles.expressDelivery}
            />
          </div>
          <PAText className={`m-0 ${styles.heading}`}>{t("footer.Tag_line_newsletter")}</PAText>
        </div>
        <div className="col-md-3 col-xs-12">
          {renderText}
          <Form
            id="login-form-newsletter"
            ref={formRef}
            layout="vertical"
            onChange={handleOnFormChange}
            fields={["email"]}
            onValidSubmit={submit}
            className={styles.input_container}
          >
            <div className="form-group">
              <Input
                validations="isEmail"
                validationError={t("email_not_valid")}
                id="email"
                name="email"
                type="email"
                placeholder={`${t("Email") }*`}
                required
              />
            </div>
            <button
              className={classNames("btn btn-block m-l-5", styles.newsletter_submit_button)}
              type="submit"
              aria-label="Submit Newsletter"
              disabled={!canSubmit}
            >
              <ArrowRight />
            </button>
          </Form>
        </div>
      </HideIf>
    </>
  );
}

export default NewsletterFooter;
